import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import './CareerScoresPage.css';
import { FaPrint } from 'react-icons/fa';
import logo from '../../assets/OG-logo-main-learning-scores.png';

// Register Chart.js components
ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

// ADD — just after the imports, before the component
const LEVEL_NOTE_META = {
  low:  { label: 'Your Result · Emerging',  color: '#ff6b6b' },
  mid:  { label: 'Your Result · Balanced',  color: '#ffd166' },
  high: { label: 'Your Result · Strong', color: '#00cc99' },
};

const LearningScoresPage = ({ userId }) => {
  const [visualizations, setVisualizations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMounted, setIsMounted] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const chartRefs = useRef({});
  const pageRef = useRef(null);

  // Remove console.logs in production
  if (process.env.NODE_ENV !== "development") {
    console.log = () => {};
  }
  
  useEffect(() => {
  setIsMounted(true);
  const currentChartRefs = chartRefs.current; // Capture the ref value

  // Track page view in Google Analytics
    if (window.gtag) {
      window.gtag('config', 'G-B8RJQ1KWE4', {
        page_path: location.pathname,
        page_title: 'Learning Scores Page',
      });
    }

  return () => {
    setIsMounted(false);
    Object.values(currentChartRefs).forEach((chart) => chart?.destroy());
  };
}, [location]);

  useEffect(() => {
    const CACHE_KEY = `visualizations_${userId}_Learning`;
    if (!userId) {
      setLoading(false);
      setError('No user ID provided');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      setError('Unauthorized: Please log in again.');
      navigate('/login');
      return;
    }

    // Check navigation state first
    if (location.state?.visualizations) {
      if (isMounted) {
        const data = location.state.visualizations;
        setVisualizations(data);
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(data)); // Cache data
        setLoading(false);
        return;
      }
    }

    // Check sessionStorage cache
    const cachedData = sessionStorage.getItem(CACHE_KEY);
    if (cachedData) {
      if (isMounted) {
        try {
          const data = JSON.parse(cachedData);
          // Validate data structure
          if (data?.socialEnvironmentalLearning?.labels?.length > 0) {
            setVisualizations(data);
            setLoading(false);
            return;
          } else {
            console.warn('Invalid cached data: missing or empty labels');
            sessionStorage.removeItem(CACHE_KEY); // Clear invalid cache
          }
        } catch (err) {
          console.warn('Error parsing cached data:', err);
          sessionStorage.removeItem(CACHE_KEY); // Clear corrupted cache
        }
      }
    }

    // No data found
    if (isMounted) {
      setError('No visualization data available. Please complete the Learning assessment.');
      setLoading(false);
    }
  }, [userId, navigate, isMounted, location.state]);

      useEffect(() => {
      document.body.classList.add('specific-page-enrich');
      return () => document.body.classList.remove('specific-page-enrich');
    });
  
  const handlePrint = () => {
    window.print();
  };

  const radarOptions = useMemo(() => ({
    scales: {
      r: {
        beginAtZero: true,
        max: 5,
        ticks: { stepSize: 1, color: 'white', backdropColor: 'transparent', font: { size: 12 } },
        pointLabels: {
          color: 'white',
          font: { size: 12 },
          padding: 10,
          callback: (label) => (label?.length > 40 ? label.substring(0, 30) + '...' : label || 'Unknown'),
        },
        grid: { color: 'rgba(255, 255, 255, 0.3)' },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (context) => `${context.dataset.label}: ${context.raw.toFixed(2)}` } },
    },
    maintainAspectRatio: false,
  }), []);

  const renderSortedScoresTable = (data) => {
    if (!data?.labels || !data?.datasets?.[0]?.data || !data?.datasets?.[0]?.totalScores) {
      return <p style={{ color: 'white' }}>No data available.</p>;
    }

    const scores = data.labels.map((label, index) => ({
      _id: label,
      averageScore: data.datasets[0].data[index] || 0,
      totalScore: data.datasets[0].totalScores[index] || 0,
      facetNote: data.datasets[0].facetNote?.[index] || null,
    })).sort((a, b) => b.averageScore - a.averageScore);

    return (
      <table
        className="scores-table"
        style={{
          width: '100%',
          color: 'white',
          fontSize: '14px',
          borderCollapse: 'collapse',
          borderRadius: '10px',
          background: 'linear-gradient(to right, #0b1021, rgb(30, 37, 58))',
        }}
      >
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '10px' }}>Facet</th>
            <th style={{ textAlign: 'right', padding: '10px' }}>Average Score</th>
            <th style={{ textAlign: 'right', padding: '10px' }}>Total Score</th>
          </tr>
        </thead>
        <tbody>
          {scores.map((score) => {
            // facetNote is now { general, level, levelKey } (or null) rather
            // than a plain string — guard against legacy string shape too.
            const noteData =
              typeof score.facetNote === 'string'
                ? { general: score.facetNote.trim() || null, level: null, levelKey: null }
                : score.facetNote || null;
            const hasGeneral = !!noteData?.general?.trim();
            const hasLevel = !!noteData?.level?.trim();
            const levelMeta = noteData?.levelKey ? LEVEL_NOTE_META[noteData.levelKey] : null;

            return (
              <React.Fragment key={score._id || Math.random()}>
                <tr>
                  <td style={{ padding: '8px 10px' }}>{score._id || 'Unknown'}</td>
                  <td style={{ textAlign: 'right', padding: '8px 10px' }}>
                    {score.averageScore ? score.averageScore.toFixed(1) : 'N/A'}
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px 10px' }}>
                    {typeof score.totalScore === 'number' ? score.totalScore.toFixed(1) : 'N/A'}
                  </td>
                </tr>
                {(hasGeneral || hasLevel) && (
                  <tr>
                    <td colSpan="3" style={{ padding: '0 10px 10px' }}>
                      {hasGeneral && (
                        <div style={{ color: '#ffffffcc', fontSize: '12px', marginBottom: hasLevel ? '8px' : 0 }}>
                          {noteData.general}
                        </div>
                      )}
                      {hasLevel && levelMeta && (
                        <div
                          style={{
                            padding: '8px 10px',
                            background: `${levelMeta.color}18`,
                            border: `1px solid ${levelMeta.color}55`,
                            borderRadius: '6px',
                          }}
                        >
                          <div
                            style={{
                              fontSize: '10px',
                              fontWeight: 700,
                              letterSpacing: '0.04em',
                              textTransform: 'uppercase',
                              color: levelMeta.color,
                              marginBottom: '3px',
                            }}
                          >
                            {levelMeta.label}
                          </div>
                          <div style={{ fontSize: '12px', color: '#ddd' }}>
                            {noteData.level}
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
  };

  const renderChart = (key, title, data) => {
    // Debug: Log the data object to verify structure
    console.log(`Chart Data for ${key}:`, data);

    // Validate data structure
    if (!data?.labels || data.labels.length === 0 || !data?.datasets?.[0]?.data) {
      console.warn(`Invalid data structure for ${key}:`, data);
      return <p style={{ color: 'white' }}>Invalid chart data for {title}. Please complete the assessment.</p>;
    }

    const height = '400px';
    const width = '700px';

    return (
      <div className="chart-section" style={{ margin: '50px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
        <h3 className="chart-heading">{title}</h3>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '20px',
            width: '100%',
            maxWidth: '1400px',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          <div className="chart-container" style={{ height, width }}>
            <Radar
              ref={(el) => {
                if (el) chartRefs.current[key] = el.chart;
                else delete chartRefs.current[key];
              }}
              data={data}
              options={radarOptions}
            />
          </div>
          <div style={{ width: '600px', maxWidth: '600px' }}>{renderSortedScoresTable(data)}</div>
        </div>
      </div>
    );
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;
  if (!visualizations) return <div>No visualization data available.</div>;

  return (
    <div className='learningscores'>
    <div ref={pageRef} style={{ padding: '0', maxWidth: '1600px', margin: '0 auto' }}>
      <header className="assessment-header-scores">
        <img
          src={logo}
          className="logo-adjustment-score"
          alt="StudyStyle Logo"
        />
        <h1 className="score-heading">StudyStyle Profile Index</h1>
        <h2 style={{ color: 'white', fontSize: '12px', marginBottom: '15px' }}>
          Learning Style & Academic Strengths
        </h2>
        <div className="header-buttons">
          <button
            className="print-button"
            onClick={handlePrint}
            title="Print as PDF"
            disabled={loading}
          >
            <FaPrint size={35} />
          </button>
          <button
            className="return-dashboard-cross"
onClick={() => {
  const returnState = location.state?.viewingChildId
    ? {
        fromLearningScores: true,
        viewingChildId: location.state.viewingChildId,
        childName: location.state.childName,
      }
    : { fromLearningScores: true };

  const returnSearch = location.state?.viewingChildId
    ? `?viewingChildId=${encodeURIComponent(location.state.viewingChildId)}&childName=${encodeURIComponent(location.state.childName || "")}`
    : "";

  navigate({ pathname: "/dashboard_enrich", search: returnSearch }, { state: returnState });
}}
            title="Return to Dashboard"
          ></button>
        </div>
      </header>
      <div className="content-container">
        {visualizations.sensoryModalityPreferences &&
          renderChart('sensoryModalityPreferences', 'Sensory Modality Preferences', visualizations.sensoryModalityPreferences)}
        {visualizations.socialEnvironmentalLearning &&
          renderChart('socialEnvironmentalLearning', 'Social & Environmental Learning Orientation', visualizations.socialEnvironmentalLearning)}
        {visualizations.selfRegulationMetacognition &&
          renderChart('selfRegulationMetacognition', 'Self-Regulation & Metacognition', visualizations.selfRegulationMetacognition)}
        {visualizations.motivationalAffectiveDispositions &&
          renderChart('motivationalAffectiveDispositions', 'Motivational & Affective Dispositions', visualizations.motivationalAffectiveDispositions)}
        {visualizations.cognitiveStyleThinkingDisposition &&
          renderChart('cognitiveStyleThinkingDisposition', 'Cognitive Style & Thinking Disposition', visualizations.cognitiveStyleThinkingDisposition)}
      </div>
    </div>
    </div>
  );
};

export default LearningScoresPage;